// Flips a Settings → Integrations card (users/_integration_card.html.haml) between
// "connected" and "not connected" in place, so a save, test or delete shows the
// owner-approved state (green Connected pill, or just "+ Add") without a reload.
// The partial renders both headers and hides one; the data-integration-card-part
// names are the contract between the two files.
import { setHidden } from "./display_toggle"
import { retoneStatusBadge } from "./status_badge"

const PART = (name) => `[data-integration-card-part="${name}"]`
const PENDING_BODY = "gl-integration-card-body--pending"

export function integrationCardFor(element) {
  return element?.closest("[data-integration-card]") || null
}

export function isIntegrationCardConnected(card) {
  return card?.dataset.integrationConnected === "true"
}

// connected — the state to show.
// status / variant — the connected pill (default green "Connected").
// collapsed — a connected card's open state. Left alone when omitted and the card
//   was already connected; a card that just became connected collapses, which is
//   the approved resting look.
export function setIntegrationCardState(card, { connected, status = "Connected", variant = "success", collapsed } = {}) {
  if (!card) return

  const wasConnected = isIntegrationCardConnected(card)
  card.dataset.integrationConnected = String(connected)

  const connectedHeader = card.querySelector(PART("connected-header"))
  const addHeader = card.querySelector(PART("add-header"))
  const bodyTop = card.querySelector(PART("body-top"))
  const body = card.querySelector(PART("body"))
  if (connectedHeader) setHidden(connectedHeader, !connected)
  if (addHeader) setHidden(addHeader, connected)
  if (bodyTop) setHidden(bodyTop, !connected)
  if (body) {
    body.classList.toggle(PENDING_BODY, !connected)
    body.classList.toggle("gl-profile-panel", !connected)
    body.classList.toggle("gl-haml-panel-body", connected)
  }

  if (connected) setPill(card, status, variant)

  let collapse = false
  if (connected) collapse = collapsed ?? (wasConnected ? card.dataset.collapsibleCollapsedValue === "true" : true)
  setCollapsed(card, collapse, { connectedHeader, body })
}

// Opens or closes a connected card (e.g. Cancel or Save inside it).
export function setIntegrationCardCollapsed(card, collapsed) {
  if (!card) return
  setCollapsed(card, collapsed, {
    connectedHeader: card.querySelector(PART("connected-header")),
    body: card.querySelector(PART("body")),
  })
}

function setPill(card, status, variant) {
  const pill = card.querySelector(PART("pill"))
  if (!pill) return
  pill.textContent = status
  retoneStatusBadge(pill, variant)
}

// The collapsible controller watches its value attribute and re-applies it; the
// direct writes keep the DOM right in the same tick (and in specs without it).
function setCollapsed(card, collapsed, { connectedHeader, body }) {
  card.dataset.collapsibleCollapsedValue = String(collapsed)
  if (body) body.hidden = collapsed
  if (connectedHeader) connectedHeader.setAttribute("aria-expanded", String(!collapsed))
}
